package com.evoapps.evoshift.domain.model

import kotlinx.datetime.LocalDate
import kotlinx.serialization.Serializable

/**
 * SH-257: `workplaces/{id}.settings.workHours`: whether the team enters their own working hours
 * ("Pracownicy wpisują swoje godziny", off by default): any of their own shifts of a past day or
 * today whose hours no manager confirmed yet and whose day is not closed (the owner's decision:
 * no window of days). functions/workHours.js (reportWorkHours) reads the same value.
 */
@Serializable
data class WorkHoursSettings(val selfEntry: Boolean = false) {
    companion object {
        /** Tolerant read of the stored value: missing is off. */
        fun of(selfEntry: Boolean?) = WorkHoursSettings(selfEntry = selfEntry ?: false)
    }
}

/** SH-257: where a shift's hours stand on the "Godziny pracy" screen. */
enum class WorkHoursStatus {
    /** A manager confirmed the times as they are now (`actual.review` matching them). */
    CONFIRMED,
    /** Over (or of a past day) and not confirmed: counts by plan in the costs until it is. */
    TO_CONFIRM,
    /** As [TO_CONFIRM], with times the person entered themselves ("Zgłoszone przez pracownika"). */
    REPORTED,
    /** An unplanned clock-in waiting for a decision (the "Do sprawdzenia" list above the day). */
    TO_DECIDE,
    /** A manager recorded that nobody came. */
    NOT_WORKED,
    /** Not over yet: nothing to confirm. */
    NOT_YET;

    /** Waiting for a manager: counted on the screen, in the day pager and in the to-do. */
    val needsManager: Boolean get() = this == TO_CONFIRM || this == REPORTED || this == TO_DECIDE
}

/** SH-257: one shift on the "Godziny pracy" screen. [clocked]: the actual times on it now (null: none). */
data class WorkHoursRow(
    val shift: Shift,
    val workerId: String?,
    val workerName: String,
    val status: WorkHoursStatus,
    val clocked: TimeRange?,
    /** The flags the last decision did not cover (SH-134), in [ClockFlag] order. */
    val flags: List<ClockFlag>
) {
    val shiftId: String get() = shift.id
    val planned: TimeRange get() = shift.plannedTime
    /** Clocked in and out (or entered): "Jak odbito" can accept them as they are. */
    val hasFullTimes: Boolean get() = clocked?.let { it.start.isNotEmpty() && it.end.isNotEmpty() } == true
    /** The plan has length: "Zgodnie z planem" can set the times to it. */
    val hasPlan: Boolean get() = planned.start.isNotEmpty() && planned.end > planned.start
}

/** SH-257: a manager's confirmation of one shift's hours, as the reviewClock function takes it. */
data class WorkHoursDecision(
    val shiftId: String,
    val action: ClockReviewAction,
    val start: String? = null,
    val end: String? = null
)

/** SH-257: a day's hours at a glance (the day pager's marker, the day's status line, the daily results' line). */
data class WorkHoursDaySummary(val shifts: Int, val toConfirm: Int, val notYet: Int) {
    /** Every shift that is over is confirmed (and there is at least one). */
    val confirmed: Boolean get() = shifts > 0 && toConfirm == 0 && notYet == 0
}

/**
 * SH-257: the rules of the "Godziny pracy" screen. A shift's hours are confirmed when a manager's
 * decision (`actual.review`, written by reviewClock) covers exactly the actual times it has now;
 * until then a past shift counts by plan in the costs (as before) and waits here. Nothing new is
 * stored: the confirmations are the SH-134 decisions, so labour cost, the pay estimate, the
 * timesheet and the exports follow from the actual times as they always did.
 */
object WorkHoursRules {
    /** The day's shifts on the screen: worked ones and no-shows (open shifts nobody worked are asked separately). */
    fun dayShifts(shifts: List<Shift>, date: String): List<Shift> =
        shifts.filter { it.date == date && (it.workerId != null || it.isNoShow || it.unplanned?.isPending == true) }
            .sortedWith(compareBy({ it.plannedTime.start }, { it.id }))

    /** A manager's decision covers the actual times as they are now. */
    fun isConfirmed(shift: Shift): Boolean {
        val actual = shift.actualTime ?: return false
        if (actual.start.isEmpty() || actual.end.isEmpty()) return false
        val review = shift.clockCheck?.review ?: return false
        return review.start.take(16) == actual.start.take(16) && review.end.take(16) == actual.end.take(16)
    }

    /** Over: a past day, or today once its planned end (or the clock-out) passed. [nowLocal]: the workplace's local ISO time. */
    fun isOver(shift: Shift, today: String, nowLocal: String): Boolean {
        if (shift.date < today) return true
        if (shift.date > today) return false
        val end = shift.actualTime?.end?.takeIf { it.isNotEmpty() } ?: shift.plannedTime.end
        return end.isNotEmpty() && end.take(16) <= nowLocal.take(16)
    }

    fun status(shift: Shift, today: String, nowLocal: String): WorkHoursStatus = when {
        shift.notWorked -> WorkHoursStatus.NOT_WORKED
        shift.unplanned?.isPending == true -> WorkHoursStatus.TO_DECIDE
        isConfirmed(shift) -> WorkHoursStatus.CONFIRMED
        !isOver(shift, today, nowLocal) -> WorkHoursStatus.NOT_YET
        shift.clockAudit?.reportedByWorker == true -> WorkHoursStatus.REPORTED
        else -> WorkHoursStatus.TO_CONFIRM
    }

    /** The day's rows, by planned start; [nameOf] names a person (the shared resolver). */
    fun rows(shifts: List<Shift>, date: String, today: String, nowLocal: String, nameOf: (String) -> String): List<WorkHoursRow> =
        dayShifts(shifts, date).map { shift ->
            val worker = shift.unplanned?.takeIf { it.isPending }?.userId ?: shift.workerId ?: shift.userId
            val reviewed = shift.clockCheck?.review?.flags.orEmpty()
            WorkHoursRow(
                shift = shift,
                workerId = worker,
                workerName = worker?.let(nameOf) ?: shift.userDisplayName.orEmpty(),
                status = status(shift, today, nowLocal),
                clocked = shift.actualTime?.takeIf { it.start.isNotEmpty() },
                flags = shift.clockCheck?.flags.orEmpty().filter { it !in reviewed }
            )
        }

    fun summary(shifts: List<Shift>, date: String, today: String, nowLocal: String): WorkHoursDaySummary {
        val day = dayShifts(shifts, date)
        val statuses = day.map { status(it, today, nowLocal) }
        return WorkHoursDaySummary(
            shifts = day.size,
            toConfirm = statuses.count { it.needsManager },
            notYet = statuses.count { it == WorkHoursStatus.NOT_YET }
        )
    }

    /** "Zgodnie z planem": the times set to the plan (a shift clocked in but not out gets the whole plan). */
    fun asPlanned(row: WorkHoursRow): WorkHoursDecision? {
        if (!row.hasPlan) return null
        val clocked = row.clocked
        return if (clocked != null && clocked.end.isEmpty()) {
            WorkHoursDecision(row.shiftId, ClockReviewAction.EDIT_TIMES, row.planned.start, row.planned.end)
        } else WorkHoursDecision(row.shiftId, ClockReviewAction.ADJUST_TO_PLAN)
    }

    /** "Jak odbito": the clocked (or entered) times accepted as they are. */
    fun asClocked(row: WorkHoursRow): WorkHoursDecision? =
        if (row.hasFullTimes) WorkHoursDecision(row.shiftId, ClockReviewAction.APPROVE) else null

    /**
     * "Potwierdź dzień": every row still waiting, as clocked where clocked in and out, the clock-in
     * with the planned end where only clocked in, else as planned. Unplanned clock-ins wait for
     * their own decision; a row without a usable plan or times is left.
     */
    fun confirmDay(rows: List<WorkHoursRow>): List<WorkHoursDecision> = rows
        .filter { it.status == WorkHoursStatus.TO_CONFIRM || it.status == WorkHoursStatus.REPORTED }
        .mapNotNull { row ->
            val clocked = row.clocked
            when {
                row.hasFullTimes -> asClocked(row)
                clocked != null && row.hasPlan && row.planned.end > clocked.start ->
                    WorkHoursDecision(row.shiftId, ClockReviewAction.EDIT_TIMES, clocked.start, row.planned.end)
                else -> asPlanned(row)
            }
        }

    /** The times a decision leaves on the shift (for the optimistic row); null when they follow from the server. */
    fun timesAfter(row: WorkHoursRow, decision: WorkHoursDecision): TimeRange? = when (decision.action) {
        ClockReviewAction.APPROVE -> row.clocked
        ClockReviewAction.ADJUST_TO_PLAN -> row.planned
        ClockReviewAction.EDIT_TIMES -> decision.start?.let { TimeRange(it, decision.end.orEmpty()) }
        else -> null
    }

    /** [row]'s shift as the decision leaves it: the times and a decision covering them (shown at once). */
    fun confirmedShift(row: WorkHoursRow, decision: WorkHoursDecision, by: String, at: String): Shift? {
        val times = timesAfter(row, decision) ?: return null
        val check = row.shift.clockCheck ?: ClockCheck()
        val kind = when (decision.action) {
            ClockReviewAction.APPROVE -> ClockDecision.APPROVED
            ClockReviewAction.ADJUST_TO_PLAN -> ClockDecision.ADJUSTED_TO_PLAN
            else -> ClockDecision.EDITED
        }
        return row.shift.copy(
            actualTime = times,
            clockCheck = check.copy(review = ClockReviewDecision(kind, check.flags, times.start, times.end, by, null, at))
        )
    }

    /**
     * Whether [userId] may enter their own hours of [shift] (SH-257 self-entry): the workplace lets
     * the team ([settings]), it is theirs, of [today] or any day before, it has started ([nowLocal]),
     * nobody recorded it as not worked and no manager has confirmed its hours yet. reportWorkHours
     * checks the same on the server, and also refuses a closed day (DAY_CLOSED).
     */
    fun selfEntryOpen(shift: Shift, userId: String, settings: WorkHoursSettings, today: LocalDate, nowLocal: String): Boolean {
        if (!settings.selfEntry || shift.notWorked || shift.workerId != userId) return false
        if (shift.unplanned?.let { it.isPending && it.userId != userId } == true) return false
        if (shift.clockCheck?.review != null) return false
        if (shift.date > today.toString()) return false
        return shift.plannedTime.start.isNotEmpty() && shift.plannedTime.start.take(16) <= nowLocal.take(16)
    }
}

/** SH-257: why reportWorkHours refused the person's own hours (the screen words it). */
class WorkHoursReportException(val reason: Reason) : Exception("Work hours report: $reason") {
    enum class Reason {
        NOT_YOURS, PLAN_ENDED, SELF_ENTRY_OFF, OUTSIDE_WINDOW, BAD_TIMES, IN_FUTURE, ALREADY_CONFIRMED, DAY_CLOSED
    }
}
